import { Link } from "react-router-dom";
import { SERVICES, PHONE_HREF, PHONE } from "../../mocks/businessData";
import TrustBar from "../../components/feature/TrustBar";
import AIAnswerBlock from "../../components/feature/AIAnswerBlock";
import ProcessSection from "../../components/feature/ProcessSection";
import WhyChooseUs from "../../components/feature/WhyChooseUs";
import ServiceAreasSection from "../../components/feature/ServiceAreasSection";
import Testimonials from "../../components/feature/Testimonials";
import FAQSection from "../../components/feature/FAQSection";
import FinalCTA from "../../components/feature/FinalCTA";

const serviceFAQs = [
  {
    q: "What landscaping services does Upper Canada Landscape offer?",
    a: "We offer comprehensive landscaping services including landscape design and construction, natural stone work and hardscaping, water feature installation (ponds, waterfalls, streams), and premium synthetic turf solutions. Every project is custom-designed for your property.",
  },
  {
    q: "How long does a landscape design and construction project take?",
    a: "Project timelines depend on scope. A stone patio installation typically takes 3-7 days, while a full backyard transformation with multiple features can take 2-5 weeks. We provide a detailed schedule during the proposal phase so you know exactly what to expect.",
  },
  {
    q: "Do you install water features in London, Ontario?",
    a: "Yes. Water feature installation is a core specialty at Upper Canada Landscape. We design and build custom ponds, natural waterfalls, streams, and pondless features for residential properties throughout London and Southwestern Ontario.",
  },
  {
    q: "Is synthetic turf safe for pets and children?",
    a: "Absolutely. Our synthetic turf products are manufactured specifically to be safe for pets and children. They are free from harmful chemicals, designed with proper drainage systems, and are significantly cooler and more durable than lower-quality alternatives.",
  },
  {
    q: "What types of stone work do you specialize in?",
    a: "Our stone work services include natural and engineered stone patios, armour stone retaining walls, flagstone walkways, interlocking paver driveways, outdoor kitchen platforms, and decorative stone features. All work is structural-grade and built to last decades.",
  },
  {
    q: "Do your services come with a warranty?",
    a: "Yes. We stand behind our work. Synthetic turf products carry a 10-year product warranty. Hardscape and stone construction includes a structural workmanship warranty. Water feature systems are backed by equipment manufacturer warranties. Ask for details during your consultation.",
  },
];

const serviceAIContent = {
  heading: "Landscaping Services in London, Ontario: Complete Guide",
  paragraphs: [
    {
      lead: "Services overview:",
      body: "Upper Canada Landscape provides four core services in London, Ontario: custom landscape design and construction, natural stone hardscaping, water feature installation, and synthetic turf solutions. All services are available for residential and select commercial properties.",
    },
    {
      lead: "Landscape design and construction:",
      body: "Full-service landscape design begins with a site consultation and custom proposal. Projects include plant selection, grading, bed installation, trees, and full landscape construction managed in-house from concept to completion.",
    },
    {
      lead: "Stone work and hardscape:",
      body: "Premium hardscape services include natural stone patios, interlocking brick driveways, armour stone retaining walls, flagstone walkways, and outdoor living platforms. All stonework is built on proper structural foundations for longevity in Ontario's climate.",
    },
    {
      lead: "Water features:",
      body: "Custom water features include ponds with aquatic planting, natural waterfalls, stream beds, and pondless waterfall systems. Systems are designed for low-maintenance operation with proper filtration, winterization, and year-round durability.",
    },
    {
      lead: "Synthetic turf:",
      body: "Professional artificial grass installations for residential lawns, pet runs, putting greens, and recreational surfaces. Premium synthetic turf is installed with proper sub-base preparation, drainage, and secured edging for a natural, long-lasting appearance.",
    },
  ],
};

export default function ServicesPage() {
  return (
    <main>
      {/* 1. Hero */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://uppercanadalandscape.com/wp-content/uploads/2026/03/Multi-Level-Backyard-Pool-Patio-and-Retaining-Wall-Landscape.webp"
            alt="Multi-level backyard landscape with stone retaining walls and pool area – Upper Canada Landscape services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20"></div>
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-16 pb-16 pt-36">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-rust-400 block"></span>
            <span className="font-sans text-xs font-semibold tracking-[0.2em] uppercase text-rust-300">
              What We Build
            </span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl font-bold text-white leading-tight mb-4 max-w-3xl">
            Landscaping Services in London, Ontario
          </h1>
          <p className="font-sans text-lg text-stone-300 max-w-2xl mb-8">
            From concept to completion &mdash; landscape design, stone hardscape, water features, and synthetic turf installed by artisan in-house crews with 30 years of experience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/#contact"
              onClick={() => {
                setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 100);
              }}
              className="whitespace-nowrap inline-flex items-center gap-2 bg-rust-700 hover:bg-rust-800 text-white font-sans text-sm font-semibold px-7 py-3.5 rounded-sm cursor-pointer transition-colors"
            >
              <i className="ri-calendar-check-fill"></i>
              Schedule a Consultation
            </Link>
            <a
              href={PHONE_HREF}
              className="whitespace-nowrap inline-flex items-center gap-2 border border-white/40 text-white font-sans text-sm font-semibold px-7 py-3.5 rounded-sm hover:bg-white/10 cursor-pointer transition-colors"
            >
              <i className="ri-phone-fill"></i>
              {PHONE}
            </a>
          </div>
        </div>
      </section>

      {/* 2. Trust Bar */}
      <TrustBar />

      {/* 3. Services Detail */}
      <section id="services-detail" className="bg-white py-20 md:py-28">
        <div className="w-full px-4 md:px-8 lg:px-16">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="font-sans text-xs font-semibold tracking-[0.2em] uppercase text-rust-600 mb-4 block">
              Our Services
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-charcoal leading-tight">
              Every Service, Artisan-Built
            </h2>
          </div>

          <div className="space-y-24">
            {SERVICES.map((service, i) => (
              <div
                key={service.id}
                id={service.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch rounded-xl overflow-hidden border border-stone-100 ${
                  i % 2 === 0 ? "" : "lg:flex-row-reverse"
                }`}
              >
                {/* Image side */}
                <div className={`relative w-full h-72 lg:h-auto ${i % 2 !== 0 ? "lg:order-2" : ""}`}>
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                {/* Content side */}
                <div className={`p-8 md:p-12 bg-white ${i % 2 !== 0 ? "lg:order-1" : ""}`}>
                  <p className="font-sans text-xs font-semibold uppercase tracking-widest text-rust-600 mb-3">
                    {service.tagline}
                  </p>
                  <h3 className="font-serif text-2xl md:text-3xl font-bold text-charcoal mb-4">
                    {service.name}
                  </h3>
                  <p className="font-sans text-base text-stone-500 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-2 mb-8">
                    {service.features.map((f) => (
                      <li key={f} className="flex items-center gap-3">
                        <i className="ri-checkbox-circle-fill text-rust-600 text-base flex-shrink-0"></i>
                        <span className="font-sans text-sm text-charcoal">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/#contact"
                    onClick={() => {
                      setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 100);
                    }}
                    className="whitespace-nowrap inline-flex items-center gap-2 bg-rust-700 hover:bg-rust-800 text-white font-sans text-sm font-semibold px-6 py-3 rounded-sm transition-colors cursor-pointer"
                  >
                    Get a Quote for This Service
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. AI Answer Block */}
      <AIAnswerBlock heading={serviceAIContent.heading} paragraphs={serviceAIContent.paragraphs} />

      {/* 5. Process */}
      <ProcessSection />

      {/* 6. Why Choose Us */}
      <WhyChooseUs />

      {/* 7. Service Areas */}
      <ServiceAreasSection />

      {/* 8. Testimonials */}
      <Testimonials />

      {/* 9. FAQ */}
      <FAQSection
        heading="Service Questions Answered"
        items={serviceFAQs}
      />

      {/* 10. Final CTA */}
      <FinalCTA
        heading="Ready to Transform Your London Property?"
        subtext="Our landscape design team is ready to walk your property and create a custom plan built around your vision. Let\u2019s talk."
        bgImage="https://uppercanadalandscape.com/wp-content/uploads/2026/03/Stone-Waterfall-Feature-and-Backyard-Patio-Installation.webp"
      />
    </main>
  );
}
